import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, IconButton, MenuItem, Avatar } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { useDispatch } from "store";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import {
    riskCategories, riskImpacts,
    riskSeverity, riskStatus, riskResponsible,
    MitigationactionStatus, riskLikelihood,
    impactCategories
} from "helpers/riskOptions";
import Select from "react-select";
import { useCreateProjectRiskMutation } from "store/slices/apis/projectRiskSlice";
import { actionCategories, actionContacts, actionDecisionVsOperational, actionPriority, actionResponsible, actionSource, actionStatus } from "helpers/actionTrackerOptions";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const calenderSVG = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_894_610)">
            <path d="M12.6667 1.33333H12V0.666667C12 0.489856 11.9298 0.320286 11.8047 0.195262C11.6797 0.0702379 11.5101 0 11.3333 0C11.1565 0 10.987 0.0702379 10.8619 0.195262C10.7369 0.320286 10.6667 0.489856 10.6667 0.666667V1.33333H5.33333V0.666667C5.33333 0.489856 5.2631 0.320286 5.13807 0.195262C5.01305 0.0702379 4.84348 0 4.66667 0C4.48986 0 4.32029 0.0702379 4.19526 0.195262C4.07024 0.320286 4 0.489856 4 0.666667V1.33333H3.33333C2.4496 1.33439 1.60237 1.68592 0.97748 2.31081C0.352588 2.93571 0.00105857 3.78294 0 4.66667L0 12.6667C0.00105857 13.5504 0.352588 14.3976 0.97748 15.0225C1.60237 15.6474 2.4496 15.9989 3.33333 16H12.6667C13.5504 15.9989 14.3976 15.6474 15.0225 15.0225C15.6474 14.3976 15.9989 13.5504 16 12.6667V4.66667C15.9989 3.78294 15.6474 2.93571 15.0225 2.31081C14.3976 1.68592 13.5504 1.33439 12.6667 1.33333ZM1.33333 4.66667C1.33333 4.13623 1.54405 3.62753 1.91912 3.25245C2.29419 2.87738 2.8029 2.66667 3.33333 2.66667H12.6667C13.1971 2.66667 13.7058 2.87738 14.0809 3.25245C14.456 3.62753 14.6667 4.13623 14.6667 4.66667V5.33333H1.33333V4.66667ZM12.6667 14.6667H3.33333C2.8029 14.6667 2.29419 14.456 1.91912 14.0809C1.54405 13.7058 1.33333 13.1971 1.33333 12.6667V6.66667H14.6667V12.6667C14.6667 13.1971 14.456 13.7058 14.0809 14.0809C13.7058 14.456 13.1971 14.6667 12.6667 14.6667Z" fill="#433F5E" />
            <path d="M8 11C8.55228 11 9 10.5523 9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10C7 10.5523 7.44772 11 8 11Z" fill="#433F5E" />
            <path d="M4.66656 11C5.21885 11 5.66656 10.5523 5.66656 10C5.66656 9.44772 5.21885 9 4.66656 9C4.11428 9 3.66656 9.44772 3.66656 10C3.66656 10.5523 4.11428 11 4.66656 11Z" fill="#433F5E" />
            <path d="M11.3334 11C11.8857 11 12.3334 10.5523 12.3334 10C12.3334 9.44772 11.8857 9 11.3334 9C10.7811 9 10.3334 9.44772 10.3334 10C10.3334 10.5523 10.7811 11 11.3334 11Z" fill="#433F5E" />
        </g>
        <defs>
            <clipPath id="clip0_894_610">
                <rect width="16" height="16" fill="white" />
            </clipPath>
        </defs>
    </svg>
);


const AddRisk = (props) => {
    const { open, handleClose, actionContact, refetchCounts } = props;
    const { projectId } = useParams();
    const dispatch = useDispatch();

    const [createProjectRisk, { isLoading }] = useCreateProjectRiskMutation();

    const module = "Risk";
    const { data: riskCategoriesRes } = useGetMasterDataQuery({ module: module, category: 'Category', ProjectId: projectId });
    const categoryData = riskCategoriesRes?.data || [];
    const riskCategories = mapAndSortAlphabetically(categoryData);


    const { data: riskResponsibleRes } = useGetMasterDataQuery({ module: module, category: 'EntityPersonResponsible', ProjectId: projectId });
    const riskResponsibleData = riskResponsibleRes?.data || [];
    const riskResponsible = mapAndSortAlphabetically(riskResponsibleData);
    // const riskCategories = (categoryData || []).map(p => ({
    //     value: p?.value,
    //     label: p?.value,
    // }));

    // const { data: riskLikelihoodRes } = useGetMasterDataQuery({ module: module, category: 'Likelihood', ProjectId: projectId });
    // const riskLikelihoodData = riskLikelihoodRes?.data || [];
    // const riskLikelihood = (riskLikelihoodData || []).map(p => ({
    //     value: p?.value,
    //     label: p?.value,
    // }));

    // const { data: riskImpactsRes } = useGetMasterDataQuery({ module: module, category: 'Impact', ProjectId: projectId });
    // const riskImpactsData = riskImpactsRes?.data || [];
    // const riskImpacts = (riskImpactsData || []).map(p => ({
    //     value: p?.value,
    //     label: p?.value,
    // }));

    const { data: riskStatusRes } = useGetMasterDataQuery({ module: module, category: 'RiskStatus', ProjectId: projectId });
    const riskStatusData = riskStatusRes?.data || [];
    const riskStatus = (riskStatusData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const { data: impactCategoriesRes } = useGetMasterDataQuery({ module: module, category: 'ImpactCategory', ProjectId: projectId });
    const impactCategoriesData = impactCategoriesRes?.data || [];
    const impactCategories = mapAndSortAlphabetically(impactCategoriesData);

    // const impactCategories = (impactCategoriesData || []).map(p => ({
    //     value: p?.value,
    //     label: p?.value,
    // }));

    const [actions, setActions] = useState([
        {
            tempId: 1,
            description: "",
            assignedTo: "",
            targetClosureDate: "",
        },
    ]);
    const [formData, setFormData] = useState({
        category: "",
        status: "",
        title: "",
        riskRating: "",
        severity: "",
        mitigationActionStatus: "",
        targetClosureDate: "",
        entityPersonResponsible: "",
        description: "",
        riskOwner: "",
        dateIdentified: "",
        likelihood: "",
        impact: "",
        impactCategory: "",
        mitigationPlan: "",
        projectId: projectId,
        riskStatus: ""
    });

    const validationSchema = Yup.object({
        category: Yup.string().required("Category field is required"),
        riskStatus: Yup.string().required("Status field is required"),
        title: Yup.string().required("Title field is required"),
        riskRating: Yup.number().required("Risk Rating field is required"),
        severity: Yup.string().required("Severity field is required"),
        // mitigationActionStatus: Yup.string().required("Mitigation Action Status field is required"),
        // targetClosureDate: Yup.date().required("Target Closure Date field is required"),
        // entityPersonResponsible: Yup.string().required("Entity/Person Responsible field is required"),
        description: Yup.string().required("Description field is required"),
        dateIdentified: Yup.date().required("Date Identified field is required"),
        riskOwner: Yup.string().required("Risk Owner field is required"),
        likelihood: Yup.number().required("Likelihood field is required"),
        impact: Yup.number().required("Impact field is required"),
        impactCategory: Yup.string().required("Impact Category field is required"),
        // mitigationPlan: Yup.string().required("Mitigation Plan field is required")
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        const projectActions = actions
            .map(a => ({
                projectId: projectId, // REQUIRED in your ProjectAction model
                topic: "Risk Management",
                description: a.description?.trim() || null,
                assignedTo: a.assignedTo || null,
                dueDate: a.dueDate ? moment(a.dueDate).toISOString() : null,
                status: "Open" // optional default
            }))
            .filter(a => a.description); // keep only filled rows

        let reqBody = {
            projectId: projectId,
            category: values?.category,
            title: values?.title,
            description: values?.description,
            dateIdentified: moment(values?.dateIdentified)?.toISOString(),
            riskOwner: values?.riskOwner,
            likelihood: Number(values?.likelihood),
            impact: Number(values?.impact),
            riskRating: Number(values?.riskRating),
            severity: values?.severity,
            impactCategory: values?.impactCategory,
            riskStatus: values?.riskStatus,
            projectActions,
            // mitigationPlan: values?.mitigationPlan,
            // mitigationActionStatus: values?.mitigationActionStatus,
            // targetClosureDate: moment(values?.targetClosureDate)?.toISOString(),
            // entityPersonResponsible: values?.entityPersonResponsible,
            remarks: 'Project Risks',
            isActive: true,
        };

        console.log(reqBody);

        try {
            const response = await createProjectRisk({ body: reqBody }).unwrap();
            openNotification("Project risk created successfully", "success");
            resetForm();
            setActions([{ tempId: 1, description: "", assignedTo: "", dueDate: "" }]);
            setSubmitting(false);
            refetchCounts()
            handleClose();
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };
    const handleActionChange = (index, field, value) => {
        setActions((prev) => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    };
    const handleAddActionRow = () => {
        setActions((prev) => [
            ...prev,
            {
                tempId: prev.length ? prev[prev.length - 1].tempId + 1 : 1,
                description: "",
                assignedTo: "",
                dueDate: "",
            },
        ]);
    };
    const handleRemoveActionRow = (index) => {
        setActions((prev) => {
            const copy = [...prev];
            copy.splice(index, 1);
            return copy;
        });
    };


    const computeSeverity = (rating) => {
        if (rating >= 15) return "Critical";
        if (rating >= 6) return "Moderate";
        return "Low";
    };

    return (
        <Dialog open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
            }}>
                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                        Add Risk
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        setFieldTouched,
                        setTouched, }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Typography sx={{ mt: 1, mb: 0, fontSize: "22px", fontWeight: 500, fontFamily: "Outfit, sans-serif", color: "#0A0A0C" }}>
                                        Risk Details
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Select Category
                                    </Typography>
                                    <Select
                                        name="category"
                                        options={riskCategories}
                                        value={riskCategories.find(opt => opt.value === String(values.category)) || null}
                                        onChange={(option) => setFieldValue("category", option.value)}
                                        onBlur={() => setFieldTouched("category", true)}
                                        placeholder="Select Category"
                                    />
                                    {touched.category && errors.category && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.category}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>Title</Typography>
                                    <TextField
                                        fullWidth
                                        name="title"
                                        value={values.title}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Risk Title"
                                        InputProps={{
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.title && errors.title && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.title}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                        Date Identified
                                    </Typography>
                                    <TextField
                                        type="date"
                                        name="dateIdentified"
                                        value={values.dateIdentified}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        onClick={(e) => {
                                            const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                            if (input) input.showPicker?.();
                                        }}
                                        fullWidth
                                        sx={{
                                            ...rightTextStyle,
                                            "& input::-webkit-calendar-picker-indicator": {
                                                display: "none",
                                                WebkitAppearance: "none",
                                            },
                                            "& input[type='date']": {
                                                appearance: "none",
                                                WebkitAppearance: "none",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    border: "none",
                                                },
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                    {calenderSVG}
                                                </Box>
                                            ),
                                            sx: {
                                                backgroundColor: "#fff",
                                                border: '1px solid',
                                                borderColor: 'hsl(0, 0%, 80%)',
                                                height: "40px",
                                                width: { xs: "100%", md: "100%" },
                                                borderRadius: "5px",
                                            },
                                        }}
                                    />
                                    {touched.dateIdentified && errors.dateIdentified && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.dateIdentified}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>Risk Owner</Typography>
                                    {/* <TextField
                                        fullWidth
                                        name="riskOwner"
                                        value={values.riskOwner}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Risk Owner"
                                        InputProps={{
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    "&:-webkit-autofill": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                    "&:-webkit-autofill:hover": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                    "&:-webkit-autofill:focus": {
                                                        WebkitBoxShadow: "0 0 0 1000px white inset",
                                                        WebkitTextFillColor: "#0A0A0C",
                                                    },
                                                },
                                            },
                                        }}
                                    /> */}
                                    <Select
                                        name="riskOwner"
                                        options={riskResponsible}
                                        value={riskResponsible.find(opt => opt.value === String(values.riskOwner)) || null}
                                        onChange={(option) => setFieldValue("riskOwner", option.value)}
                                        onBlur={() => setFieldTouched("riskOwner", true)}
                                        placeholder="Select Risk Owner"
                                    />
                                    {touched.riskOwner && errors.riskOwner && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.riskOwner}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <Typography sx={labelTextStyle}>Description</Typography>
                                    <TextField
                                        fullWidth
                                        name="description"
                                        value={values.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        multiline
                                        rows={3}
                                        placeholder="Enter Risk Description"
                                        InputProps={{
                                            sx: {
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& textarea::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& textarea": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.description && errors.description && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.description}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={12}>
                                    <Typography sx={{ mt: 1, mb: 0, fontSize: "22px", fontWeight: 500, fontFamily: "Outfit, sans-serif", color: "#0A0A0C" }}>
                                        Risk Assessment
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Likelihood
                                    </Typography>
                                    <Select
                                        name="likelihood"
                                        options={riskLikelihood}
                                        value={riskLikelihood.find(opt => opt.value === String(values.likelihood)) || null}
                                        onChange={(option) => {
                                            const likelihood = Number(option.value) || 0;
                                            const impact = Number(values.impact) || 0;
                                            const rating = likelihood * impact;

                                            setFieldValue("likelihood", option.value);
                                            setFieldValue("riskRating", rating);
                                            setFieldValue("severity", computeSeverity(rating));
                                        }}
                                        onBlur={() => setFieldTouched("likelihood", true)}
                                        placeholder="Select Likelihood"
                                    />
                                    {touched.likelihood && errors.likelihood && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.likelihood}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Select Risk Impact
                                    </Typography>
                                    <Select
                                        name="impact"
                                        options={riskImpacts}
                                        value={riskImpacts.find(opt => opt.value === String(values.impact)) || null}
                                        onChange={(option) => {
                                            const impact = Number(option.value) || 0;
                                            const likelihood = Number(values.likelihood) || 0;
                                            const rating = likelihood * impact;

                                            setFieldValue("impact", option.value);
                                            setFieldValue("riskRating", rating);
                                            setFieldValue("severity", computeSeverity(rating));
                                        }}
                                        onBlur={() => setFieldTouched("impact", true)}
                                        placeholder="Select Risk Impact"
                                    />
                                    {touched.impact && errors.impact && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.impact}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>RiskRating</Typography>
                                    <TextField
                                        fullWidth
                                        name="riskRating"
                                        value={values.riskRating}
                                        // no onChange -> user cannot edit manually
                                        InputProps={{
                                            readOnly: true,
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            }
                                        }}
                                        placeholder="Auto-calculated"

                                    />
                                    {touched.riskRating && errors.riskRating && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.riskRating}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Select Severity
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="severity"
                                        value={values.severity}
                                        InputProps={{
                                            readOnly: true,
                                            sx: {
                                                height: "40px",
                                                borderRadius: "8px",
                                                backgroundColor: "#f3f3f3",
                                                fontWeight: 600,
                                                color:
                                                    values.severity === "Critical" ? "#FF383C" :
                                                        values.severity === "Moderate" ? "#FFA500" :
                                                            "#4CAF50",
                                            },
                                        }}
                                    />
                                    {touched.severity && errors.severity && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.severity}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Impact Category
                                    </Typography>
                                    <Select
                                        name="impactCategory"
                                        options={impactCategories}
                                        value={impactCategories.find(opt => opt.value === String(values.impactCategory)) || null}
                                        onChange={(option) => setFieldValue("impactCategory", option.value)}
                                        onBlur={() => setFieldTouched("impactCategory", true)}
                                        placeholder="Select Impact Category"
                                    />
                                    {touched.impactCategory && errors.impactCategory && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.impactCategory}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Risk Status
                                    </Typography>
                                    <Select
                                        name="riskStatus"
                                        options={riskStatus}
                                        value={riskStatus.find(opt => opt.value === String(values.riskStatus)) || null}
                                        onChange={(option) => setFieldValue("riskStatus", option.value)}
                                        onBlur={() => setFieldTouched("riskStatus", true)}
                                        placeholder="Select Status"
                                    />
                                    {touched.riskStatus && errors.riskStatus && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.riskStatus}
                                        </Typography>
                                    )}

                                </Grid>
                                {/* <Grid size={12}>
                                    <Typography sx={{ mt: 1, mb: 0, fontSize: "22px", fontWeight: 500, fontFamily: "Outfit, sans-serif", color: "#0A0A0C" }}>
                                        Risk Response Planning
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                        Mitigation Action Status
                                    </Typography>
                                    <Select
                                        name="mitigationActionStatus"
                                        options={MitigationactionStatus}
                                        value={MitigationactionStatus.find(opt => opt.value === String(values.mitigationActionStatus)) || null}
                                        onChange={(option) => setFieldValue("mitigationActionStatus", option.value)}
                                        onBlur={() => setFieldTouched("mitigationActionStatus", true)}
                                        placeholder="Select Mitigation Action Status"
                                    />
                                    {touched.mitigationActionStatus && errors.mitigationActionStatus && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.mitigationActionStatus}
                                        </Typography>
                                    )}

                                </Grid>


                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                        Target Closure Date
                                    </Typography>
                                    <TextField
                                        type="date"
                                        name="targetClosureDate"
                                        value={values.targetClosureDate}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        onClick={(e) => {
                                            const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                            if (input) input.showPicker?.();
                                        }}
                                        fullWidth
                                        sx={{
                                            ...rightTextStyle,
                                            "& input::-webkit-calendar-picker-indicator": {
                                                display: "none",
                                                WebkitAppearance: "none",
                                            },
                                            "& input[type='date']": {
                                                appearance: "none",
                                                WebkitAppearance: "none",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    border: "none",
                                                },
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}>
                                                    {calenderSVG}
                                                </Box>
                                            ),
                                            sx: {
                                                backgroundColor: "#fff",
                                                border: '1px solid',
                                                borderColor: 'hsl(0, 0%, 80%)',
                                                height: "40px",
                                                width: { xs: "100%", md: "100%" },
                                                borderRadius: "5px",
                                            },
                                        }}
                                    />
                                    {touched.targetClosureDate && errors.targetClosureDate && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.targetClosureDate}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography sx={labelTextStyle}>Entity Person Responsible</Typography>
                                    <Select
                                        name="entityPersonResponsible"
                                        options={riskResponsible}
                                        value={riskResponsible.find(opt => opt.value === values.entityPersonResponsible) || null}
                                        onChange={(option) => setFieldValue("entityPersonResponsible", option.value)}
                                        onBlur={() => setFieldTouched("entityPersonResponsible", true)}
                                        placeholder="Select Responsible / Owner"
                                    />

                                    {touched.entityPersonResponsible && errors.entityPersonResponsible && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.entityPersonResponsible}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <Typography sx={labelTextStyle}>Mitigation Plan</Typography>
                                    <TextField
                                        fullWidth
                                        name="mitigationPlan"
                                        value={values.mitigationPlan}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        multiline
                                        rows={3}
                                        placeholder="Enter Mitigation Plan"
                                        InputProps={{
                                            sx: {
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",
                                                "& textarea::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },
                                                "& textarea": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.mitigationPlan && errors.mitigationPlan && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.mitigationPlan}
                                        </Typography>
                                    )}
                                </Grid> */}
                                {/* --- Action Trackers (for new Risk) --- */}
                                <Grid size={12}>
                                    <Typography
                                        sx={{
                                            mt: 3,
                                            mb: 1,
                                            fontSize: "22px",
                                            fontWeight: 500,
                                            fontFamily: "Outfit, sans-serif",
                                            color: "#0A0A0C",
                                        }}
                                    >
                                        Risk Response Planning
                                    </Typography>
                                </Grid>

                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            mb: 1,
                                        }}
                                    >
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={handleAddActionRow}
                                            sx={{
                                                textTransform: "none",
                                                borderRadius: "3px",
                                                fontFamily: "Outfit, sans-serif",
                                            }}
                                            size="small"
                                            type="button"
                                        >
                                            Add Mitigation Plan
                                        </Button>
                                    </Box>
                                </Grid>

                                {actions.map((action, index) => (
                                    <Grid
                                        key={action.tempId}
                                        size={12}
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            p: 2,
                                            mb: 2,
                                            backgroundColor: "#fafafa",
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12 }}>
                                                <Typography sx={labelTextStyle}>Mitigation Plan Description</Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={action.description}
                                                    onChange={(e) =>
                                                        handleActionChange(index, "description", e.target.value)
                                                    }
                                                    placeholder="Enter Mitigation Plan Description"
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>Assigned To</Typography>
                                                <Select
                                                    //name="assignedTo"
                                                    isMulti
                                                    options={actionContact}
                                                    value={
                                                        action.assignedTo
                                                            ? action.assignedTo.split(",")
                                                                .map(v => actionContact?.find(opt => opt?.value === v))
                                                                .filter(Boolean)
                                                            : []
                                                    }
                                                    onChange={(selectedOptions) => {
                                                        const labels = (selectedOptions || []).map(opt => opt.value).join(",");
                                                        handleActionChange(index, "assignedTo", labels);   // ✅ store in actions state
                                                    }}
                                                    onBlur={() => setFieldTouched("assignedTo", true)}
                                                    placeholder="Select Assigned To"
                                                />
                                            </Grid>


                                            <Grid size={{ xs: 10, md: 5 }}>
                                                <Typography sx={labelTextStyle}>Target Closure Date</Typography>
                                                <TextField
                                                    type="date"
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={action.dueDate}
                                                    onChange={(e) =>
                                                        handleActionChange(index, "dueDate", e.target.value)
                                                    }
                                                />
                                            </Grid>

                                            <Grid
                                                size={{ xs: 2, md: 1 }}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-end",
                                                    justifyContent: "flex-end",
                                                }}
                                            >
                                                {actions.length > 1 && (
                                                    <IconButton
                                                        onClick={() => handleRemoveActionRow(index)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}

                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Button onClick={handleClose}
                                            sx={{
                                                textTransform: "none",
                                                color: "#F15744",
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                borderRadius: "25px",
                                                fontWeight: 500,
                                                px: "20px",
                                                py: "15px",
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            // loading={isLoading}
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "20px",
                                                py: "15px",
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default AddRisk;